import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, Input, Row, Select, Space, Statistic, message } from "antd";
import PageTitle from "../../components/PageTitle";
import ListToolbar from "../../components/ListToolbar";
import ResizableTable from "../../components/ResizableTable";
import { getHouseRoomStats } from "../../api";
import { exportToCSV } from "../../utils/exportUtils";

const emptySummary = {
  house_count: 0,
  room_total: 0,
  empty: 0,
  rented: 0,
  tenant_agreed: 0,
  identity_submitted: 0,
  identity_passed: 0,
  electric_count: 0,
  water_count: 0,
};

/** 筛选项：只看该字段不为 0 的房源 */
const NON_ZERO_FILTER_OPTIONS = [
  { value: "rented", label: "房东已登记" },
  { value: "tenant_agreed", label: "租客已同意入住" },
  { value: "identity_submitted", label: "身份证已提交" },
  { value: "identity_passed", label: "身份证已通过" },
  { value: "electric_count", label: "电表" },
  { value: "water_count", label: "水表" },
];

function sumRows(rows) {
  const s = { ...emptySummary, house_count: rows.length };
  for (const h of rows) {
    s.room_total += Number(h.room_total || 0);
    s.empty += Number(h.empty || 0);
    s.rented += Number(h.rented || 0);
    s.tenant_agreed += Number(h.tenant_agreed || 0);
    s.identity_submitted += Number(h.identity_submitted || 0);
    s.identity_passed += Number(h.identity_passed || 0);
    s.electric_count += Number(h.electric_count || 0);
    s.water_count += Number(h.water_count || 0);
  }
  return s;
}

const HouseRoomStats = () => {
  const [loading, setLoading] = useState(false);
  const [houses, setHouses] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [fangdongUserId, setFangdongUserId] = useState("");
  const [houseId, setHouseId] = useState("");
  const [nonZeroFields, setNonZeroFields] = useState([]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      const fd = String(fangdongUserId || "").trim();
      const hid = String(houseId || "").trim();
      if (fd) params.fangdong_user_id = fd;
      if (hid) params.house_id = hid;
      const res = await getHouseRoomStats(params);
      if (res?.success) {
        setHouses(Array.isArray(res.data?.houses) ? res.data.houses : []);
      } else {
        setHouses([]);
        message.error(res?.error || "加载统计失败");
      }
    } catch (err) {
      const serverMsg = err.response?.data?.error || err.response?.data?.message;
      message.error(serverMsg ? `加载统计失败：${serverMsg}` : "加载统计失败");
      setHouses([]);
    } finally {
      setLoading(false);
    }
  }, [fangdongUserId, houseId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const filteredHouses = useMemo(() => {
    let list = houses;
    if (nonZeroFields.length > 0) {
      list = list.filter((h) =>
        nonZeroFields.every((field) => Number(h?.[field] || 0) > 0)
      );
    }
    const kw = String(keyword || "").trim().toLowerCase();
    if (!kw) return list;
    return list.filter((h) => {
      const name = String(h.house_name || "").toLowerCase();
      const addr = String(h.address || "").toLowerCase();
      const id = String(h.house_id || "");
      const fd = String(h.fangdong_user_id || "");
      return name.includes(kw) || addr.includes(kw) || id.includes(kw) || fd.includes(kw);
    });
  }, [houses, keyword, nonZeroFields]);

  const summary = useMemo(() => sumRows(filteredHouses), [filteredHouses]);

  const handleReset = () => {
    setKeyword("");
    setFangdongUserId("");
    setHouseId("");
    setNonZeroFields([]);
  };

  const handleExport = () => {
    const exportData = filteredHouses.map((h) => ({
      房源ID: h.house_id,
      房源名称: h.house_name,
      地址: h.address,
      房东ID: h.fangdong_user_id,
      房间总数: h.room_total,
      空房: h.empty,
      房东已登记: h.rented,
      租客已同意入住: h.tenant_agreed,
      身份证已提交: h.identity_submitted,
      身份证已通过: h.identity_passed,
      电表数: h.electric_count,
      水表数: h.water_count,
    }));
    if (exportToCSV(exportData, "房源房间统计")) {
      message.success(`成功导出 ${exportData.length} 条数据`);
    } else {
      message.error("导出失败");
    }
  };

  const columns = [
    { title: "房源ID", dataIndex: "house_id", width: 90 },
    { title: "房源名称", dataIndex: "house_name", width: 160, ellipsis: true },
    { title: "地址", dataIndex: "address", width: 200, ellipsis: true },
    { title: "房东ID", dataIndex: "fangdong_user_id", width: 100 },
    { title: "房间总数", dataIndex: "room_total", width: 90 },
    { title: "空房", dataIndex: "empty", width: 70 },
    { title: "房东已登记", dataIndex: "rented", width: 110 },
    { title: "租客已同意入住", dataIndex: "tenant_agreed", width: 130 },
    { title: "身份证已提交", dataIndex: "identity_submitted", width: 120 },
    { title: "身份证已通过", dataIndex: "identity_passed", width: 120 },
    { title: "电表", dataIndex: "electric_count", width: 70 },
    { title: "水表", dataIndex: "water_count", width: 70 },
  ];

  return (
    <div>
      <PageTitle title="房源统计" />
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={6} lg={4}><Card size="small"><Statistic title="房源数" value={summary.house_count} /></Card></Col>
        <Col xs={12} sm={8} md={6} lg={4}><Card size="small"><Statistic title="房间总数" value={summary.room_total} /></Card></Col>
        <Col xs={12} sm={8} md={6} lg={4}><Card size="small"><Statistic title="空房" value={summary.empty} /></Card></Col>
        <Col xs={12} sm={8} md={6} lg={4}><Card size="small"><Statistic title="房东已登记" value={summary.rented} /></Card></Col>
        <Col xs={12} sm={8} md={6} lg={4}><Card size="small"><Statistic title="租客已同意入住" value={summary.tenant_agreed} /></Card></Col>
        <Col xs={12} sm={8} md={6} lg={4}><Card size="small"><Statistic title="身份证已提交" value={summary.identity_submitted} /></Card></Col>
        <Col xs={12} sm={8} md={6} lg={4}><Card size="small"><Statistic title="身份证已通过" value={summary.identity_passed} /></Card></Col>
        <Col xs={12} sm={8} md={6} lg={4}><Card size="small"><Statistic title="电表" value={summary.electric_count} /></Card></Col>
        <Col xs={12} sm={8} md={6} lg={4}><Card size="small"><Statistic title="水表" value={summary.water_count} /></Card></Col>
      </Row>

      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          allowClear
          style={{ width: 160 }}
          placeholder="房东ID"
          value={fangdongUserId}
          onChange={(e) => setFangdongUserId(e.target.value)}
          onPressEnter={fetchStats}
        />
        <Input
          allowClear
          style={{ width: 160 }}
          placeholder="房源ID"
          value={houseId}
          onChange={(e) => setHouseId(e.target.value)}
          onPressEnter={fetchStats}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 360 }}
          placeholder="只看不为 0（可多选，同时满足）"
          options={NON_ZERO_FILTER_OPTIONS}
          value={nonZeroFields}
          onChange={setNonZeroFields}
          maxTagCount="responsive"
        />
      </Space>

      <ListToolbar
        searchPlaceholder="本地筛选：名称/地址/房源ID/房东ID"
        searchValue={keyword}
        onSearchChange={setKeyword}
        onSearch={() => {}}
        onReset={handleReset}
        onRefresh={fetchStats}
        onExport={handleExport}
      />

      <ResizableTable
        rowKey="house_id"
        loading={loading}
        columns={columns}
        dataSource={filteredHouses}
        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `共 ${t} 套` }}
        scroll={{ x: 1500 }}
      />
    </div>
  );
};

export default HouseRoomStats;
